/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { LitElement, PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { live } from 'lit/directives/live.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { iconExclamationTriangle, iconTrashCan } from '@/components/icons';
import { baseStyles, formStyles, rootVariablesStyles } from '@/styles';
import { QuantityInputMixin } from '../../quantity/quantity-input-mixin';

@customElement('fg-quantity-variants-input')
export class FgQuantityVariantInput extends QuantityInputMixin(LitElement) {
  @property({ type: Number })
  index!: number;

  @property({ type: Boolean })
  invalid? = true;

  @property({ type: Boolean })
  isQuantityAvailableForPurchase?: boolean;

  @property({ type: Boolean })
  canOrderWithoutStock?: boolean;

  @state()
  toggleWarning = false;

  inputElement: Ref<HTMLInputElement> = createRef();

  firstUpdated(): void {
    this.inputElement.value?.addEventListener('animationend', () => {
      this.invalid = false;
    });
  }

  willUpdate(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ) {
    this.individualQuantityCheck();
    if (changedProperties.has('quantity')) {
      if (this.quantity === 0 && this.data.quantity > 0) {
        this.data.quantity = 0;
        this.dispatchEvent(new CustomEvent('reset-quantity'));
      }
    }
  }

  individualQuantityCheck() {
    if (this.canOrderWithoutStock) {
      return;
    }
    if (!this.isQuantityAvailableForPurchase && this.data.quantity > 0) {
      this.toggleWarning = true;
      this.setConfiguratorValidation(false);
      return;
    }
    this.toggleWarning = false;
    this.setConfiguratorValidation(true);
  }

  setConfiguratorValidation(valid: boolean): void {
    this.dispatchEvent(
      new CustomEvent('quantity-stock-validation', {
        detail: { valid: valid },
      }),
    );
  }

  toggleParentActiveState() {
    this.dispatchEvent(
      new CustomEvent('quantity-change', {
        detail: {
          quantity: this.data.quantity,
          index: this.index,
          collapsed: true,
        },
      }),
    );
  }

  render() {
    const value = this.data.quantity > 0 ? this.data.quantity.toString() : '';
    const initialValue = this.quantity > 0 ? this.quantity.toString() : '';

    return html`
      <div class="input-area">
        <div class="input-wrapper">
          <input
            ${ref(this.inputElement)}
            class=${classMap({
              invalid: this.invalid !== undefined && this.invalid,
              warning: this.toggleWarning,
            })}
            placeholder="0"
            .value=${live(this.firstInteracted ? value : initialValue)}
            ?disabled=${this.disabled}
            @input=${(e: KeyboardEvent) => {
              this.onChangeQuantity(e);
              this.toggleParentActiveState();
            }}
          />
          ${this.toggleWarning ? iconExclamationTriangle : nothing}
        </div>
        <span>
          ${this.data.quantity > 0 || this.quantity > 0
            ? html`
                <button
                  @click=${() => {
                    this.setQuantity(0);
                    this.data.quantity = 0;
                    this.toggleParentActiveState();
                  }}
                >
                  ${iconTrashCan}
                </button>
              `
            : ''}
        </span>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    formStyles,
    rootVariablesStyles,
    css`
      .input-area {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .input-wrapper {
        position: relative;
      }

      .input-wrapper svg {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        width: 17px;
        height: 17px;
        color: var(--fg-warning, var(--fg-fallback-warning));
      }

      :host([part='wholesale']) input {
        width: 115px;
      }

      :host([part='wholesale']) input.warning {
        padding-right: 40px;
      }

      span {
        display: block;
        width: 17px;
      }

      button {
        color: var(--fg-gray--700);
        display: flex;
      }

      button svg {
        width: 17px;
        height: 17px;
      }
    `,
  ];
}
